import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LinkedIn as LinkedInIcon,
  ContentCopy as CopyIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';

function getDisplayName(contact) {
  return (
    contact.fullName ||
    contact.displayName ||
    `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
    'Contact'
  );
}

function getEmails(contact) {
  if (contact.emailAddresses?.length) {
    return contact.emailAddresses.map((entry) => ({
      value: entry.email,
      label: entry.emailType || 'email',
    }));
  }

  const emails = [];
  if (contact.workEmail) emails.push({ value: contact.workEmail, label: 'work' });
  if (contact.email && contact.email !== contact.workEmail) {
    emails.push({ value: contact.email, label: 'email' });
  }
  return emails.filter((entry) => entry.value);
}

function getPhones(contact) {
  if (contact.phoneNumbers?.length) {
    return contact.phoneNumbers.map((entry) => ({
      value: entry.number,
      label: entry.phoneType || 'phone',
    }));
  }

  const phones = [];
  if (contact.mobilePhone) phones.push({ value: contact.mobilePhone, label: 'mobile' });
  if (contact.directPhone && contact.directPhone !== contact.mobilePhone) {
    phones.push({ value: contact.directPhone, label: 'direct' });
  }
  if (contact.phone && !phones.some((entry) => entry.value === contact.phone)) {
    phones.push({ value: contact.phone, label: 'phone' });
  }
  return phones.filter((entry) => entry.value);
}

function getLinkedInUrl(contact) {
  return contact.linkedinUrl || contact.linkedin || contact.socialLinks?.linkedin || null;
}

const EnrichedContactPanel = ({
  contact,
  creditsUsed = 0,
  onClose,
  onCopy,
  onAddOutreach,
  getContactAvatarText,
  embedded = false,
}) => {
  if (!contact) return null;

  const displayName = getDisplayName(contact);
  const jobTitle = contact.jobTitle || contact.title || '';
  const company =
    contact.company?.name || contact.companyName || contact.company || '';
  const emails = getEmails(contact);
  const phones = getPhones(contact);
  const linkedInUrl = getLinkedInUrl(contact);

  const renderActionRow = (key, icon, primary, href, method, detail, copyLabel) => (
    <ListItem
      key={key}
      disablePadding
      sx={{
        px: 1.5,
        py: 0.75,
        alignItems: 'center',
      }}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
      <ListItemText
        primary={
          href ? (
            <Link
              href={href}
              target={method === 'linkedin' ? '_blank' : undefined}
              rel={method === 'linkedin' ? 'noopener noreferrer' : undefined}
              underline="hover"
              sx={{ fontWeight: 500, wordBreak: 'break-all' }}
            >
              {primary}
            </Link>
          ) : (
            primary
          )
        }
        primaryTypographyProps={{ variant: 'body2' }}
      />
      <Tooltip title="Copy">
        <IconButton size="small" onClick={() => onCopy(detail, copyLabel)} aria-label={`Copy ${copyLabel}`}>
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {onAddOutreach && (
        <Tooltip title="Log outreach">
          <IconButton
            size="small"
            onClick={() => onAddOutreach(contact, method, detail)}
            aria-label={`Log outreach via ${method}`}
          >
            <PersonAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </ListItem>
  );

  return (
    <Paper
      elevation={embedded ? 0 : 1}
      sx={{
        mb: embedded ? 0 : 2,
        borderRadius: embedded ? 0 : 2,
        overflow: 'hidden',
        border: embedded ? 0 : 1,
        borderColor: 'divider',
        boxShadow: embedded ? 'none' : undefined,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          bgcolor: 'grey.50',
        }}
      >
        <Avatar sx={{ width: 40, height: 40, bgcolor: 'success.main', fontSize: '0.875rem' }}>
          {getContactAvatarText ? getContactAvatarText(contact) : displayName.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
            {displayName}
          </Typography>
          {jobTitle && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {jobTitle}
            </Typography>
          )}
          {company && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {typeof company === 'string' ? company : company.name || ''}
            </Typography>
          )}
          {creditsUsed > 0 && (
            <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
              {creditsUsed} contact credit{creditsUsed === 1 ? '' : 's'} used
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close contact details">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      <List dense disablePadding sx={{ py: 0.5 }}>
        {emails.map((entry, index) =>
          renderActionRow(
            `email-${entry.value}-${index}`,
            <EmailIcon fontSize="small" color="primary" />,
            entry.value,
            `mailto:${entry.value}`,
            'email',
            entry.value,
            'Email'
          )
        )}
        {phones.map((entry, index) =>
          renderActionRow(
            `phone-${entry.value}-${index}`,
            <PhoneIcon fontSize="small" color="success" />,
            entry.value,
            `tel:${entry.value.replace(/\s/g, '')}`,
            'phone',
            entry.value,
            'Phone'
          )
        )}
        {linkedInUrl &&
          renderActionRow(
            'linkedin',
            <LinkedInIcon fontSize="small" sx={{ color: '#0077b5' }} />,
            'LinkedIn profile',
            linkedInUrl,
            'linkedin',
            linkedInUrl,
            'LinkedIn URL'
          )}
        {emails.length === 0 && phones.length === 0 && !linkedInUrl && (
          <ListItem sx={{ px: 1.5, py: 1 }}>
            <ListItemText
              primary="No contact methods available"
              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
            />
          </ListItem>
        )}
      </List>
    </Paper>
  );
};

export default EnrichedContactPanel;
